import React, { useState, useEffect } from "react";
import {
  AddButton,
  BackButton,
  SaveButton,
} from "../../../components/btn/Buttons";
import PageHeading from "../../../components/PageHeading";
import Table from "../../../components/table/Table";
import { DeleteIcon, base_url } from "../../../utils/utils";
import Input from "../../../components/inputs/Input";
import Dropdown from "../../../components/inputs/Dropdown";
import moment from "moment";
import SmallModal from "../../../components/modal/SmallModal";
import { Pagination2 } from "../../../components/pagination/pagination";
import { useSelector } from "react-redux";

const MasterDataAssets = () => {
  const [open, setOpen] = useState(false);
  const [addAssetData, setAddAssetData] = useState([]);
  const [alerts, setAlerts] = useState(false);
  const [alertsMess, setAlertsMess] = useState("");
  const [loading, setLoading] = useState(false);
  const [assetListData, setAssetListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [entries, setEntries] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [assetTypes, setAssetTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);

  const { user_id, today, emp_id, user_type } = useSelector(
    (state) => state.other
  );

  // Fetch asset types from API
  const fetchAssetTypes = async () => {
    try {
      setTypesLoading(true);
      const formData = new FormData();
      formData.append("action", "type_list");

      const response = await fetch(
        `${base_url}financial_reports/master_data.php`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success && result.data) {
        setAssetTypes(result.data);
      } else {
        console.error("Failed to fetch asset types:", result.message);
        setAssetTypes([]);
      }
    } catch (error) {
      console.error("Error fetching asset types:", error);
      setAssetTypes([]);
    } finally {
      setTypesLoading(false);
    }
  };

  // Fetch assets list from API
  const fetchAssetsList = async (page = 1, limit = 10, search = "") => {
    try {
      setListLoading(true);

      if (!user_id) {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "red", textAlign: "left" }}>
            <p>Error: Agency ID not found in localStorage</p>
          </div>
        );
        setListLoading(false);
        return;
      }

      // Create FormData
      const formData = new FormData();
      formData.append("action", "list");
      formData.append("agencyid", user_id);
      formData.append("page", page);
      formData.append("limit", limit);
      if (search) {
        formData.append("search", search);
      }

      // Make API call
      const response = await fetch(
        `${base_url}financial_reports/master_data.php`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success && result.data) {
        setAssetListData(result.data);
        setTotalCount(result.totalcount || 0);
        setPageNo(page);
      } else {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "orange", textAlign: "left" }}>
            <p>{result.message || "No assets found"}</p>
          </div>
        );
        setAssetListData([]);
      }
    } catch (error) {
      console.error("Error fetching assets list:", error);
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "red", textAlign: "left" }}>
          <p>Error: {error.message || "Failed to fetch assets list"}</p>
        </div>
      );
    } finally {
      setListLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchAssetsList(pageNo, parseInt(entries), searchValue);
    fetchAssetTypes();
  }, []);

  // API call to save asset data
  const submitAssetData = async (assetData) => {
    try {
      setLoading(true);

      if (!user_id) {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "red", textAlign: "left" }}>
            <p>Error: Agency ID not found in localStorage</p>
          </div>
        );
        setLoading(false);
        return;
      }

      // Create FormData
      const formData = new FormData();
      formData.append("action", "create");
      formData.append("agencyid", user_id);
      formData.append("asset_name", assetData.asset_name);
      formData.append("asset_description", assetData.asset_description);
      formData.append("asset_type", assetData.asset_type);
      formData.append("asset_value", assetData.asset_value);
      formData.append("asset_dep_percent", assetData.asset_dep_percent);
      if (emp_id) {
        formData.append("emp_code", emp_id);
      }

      // Make API call
      const response = await fetch(
        `${base_url}financial_reports/master_data.php`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "green", textAlign: "left" }}>
            <p>{result.message || "Asset data saved successfully!"}</p>
          </div>
        );
        return true;
      } else {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "red", textAlign: "left" }}>
            <p>{result.message || "Error saving asset data"}</p>
          </div>
        );
        return false;
      }
    } catch (error) {
      console.error("Error submitting asset data:", error);
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "red", textAlign: "left" }}>
          <p>Error: {error.message || "Failed to save asset data"}</p>
        </div>
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const validateData = () => {
    const errors = [];
    let count = 1;
    for (let asset of addAssetData) {
      if (!asset?.asset_name || asset?.asset_name.trim().length === 0) {
        errors.push(`Please enter Asset Name in Row ${count}`);
      }
      if (
        !asset?.asset_description ||
        asset?.asset_description.trim().length === 0
      ) {
        errors.push(`Please enter Asset Description in Row ${count}`);
      }
      if (!asset?.asset_type || asset?.asset_type.trim().length === 0) {
        errors.push(`Please enter Asset Type in Row ${count}`);
      }
      if (!asset?.asset_value || asset?.asset_value.trim().length === 0) {
        errors.push(`Please enter Asset Value in Row ${count}`);
      }
      if (
        !asset?.asset_dep_percent ||
        asset?.asset_dep_percent.trim().length === 0
      ) {
        errors.push(`Please enter Depreciation % in Row ${count}`);
      }
      count++;
    }
    return errors;
  };

  const handleAddAsset = () => {
    const defaultAsset = {
      id: addAssetData.length + 1,
      asset_name: "",
      asset_description: "",
      asset_type: "",
      asset_value: "",
      asset_dep_percent: "",
    };

    if (!addAssetData.length || validateData().length === 0) {
      setAddAssetData((prevData) => [...prevData, defaultAsset]);
    } else {
      const validationErrors = validateData();
      setAlerts(true);
      setAlertsMess(
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          {validationErrors.map((item, i) => (
            <p style={{ textAlign: "left" }} key={i}>
              {item}
            </p>
          ))}
        </div>
      );
    }
  };

  const handleSaveAssets = async () => {
    const validationErrors = validateData();

    if (validationErrors.length > 0) {
      setAlerts(true);
      setAlertsMess(
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          {validationErrors.map((item, i) => (
            <p style={{ textAlign: "left" }} key={i}>
              {item}
            </p>
          ))}
        </div>
      );
      return;
    }

    // Save each asset to the API
    let successCount = 0;
    let failureCount = 0;

    for (const asset of addAssetData) {
      const success = await submitAssetData(asset);
      if (success) {
        successCount++;
      } else {
        failureCount++;
      }
    }

    // Show summary
    if (failureCount === 0 && successCount > 0) {
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "green", textAlign: "left" }}>
          <p>✓ All {successCount} asset(s) saved successfully!</p>
        </div>
      );
      // Clear the form and refresh list after successful save
      setTimeout(() => {
        setAddAssetData([]);
        setOpen(false);
        fetchAssetsList(1, parseInt(entries), "");
      }, 2000);
    } else if (successCount > 0 && failureCount > 0) {
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "orange", textAlign: "left" }}>
          <p>
            ⚠ {successCount} asset(s) saved, but {failureCount} failed
          </p>
        </div>
      );
    }
  };

  const handleAddDelete = (deleteId) => {
    const filterData = addAssetData.filter((item) => item.id !== deleteId);
    setAddAssetData(filterData);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setPageNo(pageNumber);
    fetchAssetsList(pageNumber, parseInt(entries), searchValue);
  };

  // Handle search
  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPageNo(1);
    fetchAssetsList(1, parseInt(entries), value);
  };

  // Fetch asset details and auto-fill fields
  const fetchAssetDetails = async (assetTypeId, rowIndex) => {
    try {
      if (!assetTypeId) {
        // Clear fields if asset type is empty
        setAddAssetData((prevData) => {
          const updatedData = [...prevData];
          updatedData[rowIndex] = {
            ...updatedData[rowIndex],
            asset_value: "",
            asset_dep_percent: "",
          };
          return updatedData;
        });
        return;
      }

      if (!user_id) return;

      // Clear fields first while fetching
      setAddAssetData((prevData) => {
        const updatedData = [...prevData];
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          asset_value: "",
          asset_dep_percent: "",
        };
        return updatedData;
      });

      const formData = new FormData();
      formData.append("action", "fetch");
      formData.append("agencyid", user_id);
      formData.append("asset_id", assetTypeId);

      const response = await fetch(
        `${base_url}financial_reports/depreciation_account.php`,
        { method: "POST", body: formData }
      );

      const result = await response.json();

      if (result.success && result.data) {
        // Auto-fill the asset_value and asset_dep_percent fields with fresh data (formatted to 2 decimals)
        const assetValue = parseFloat(result.data.ASSET_VALUE || 0).toFixed(2);
        const depPercent = parseFloat(
          result.data.ASSET_DEP_PERCENT || 0
        ).toFixed(2);

        setAddAssetData((prevData) => {
          const updatedData = [...prevData];
          updatedData[rowIndex] = {
            ...updatedData[rowIndex],
            // asset_value: assetValue || "",
            asset_dep_percent: depPercent || "",
          };
          return updatedData;
        });
      } else {
        // If fetch fails, keep fields empty
        console.warn("Failed to fetch asset details:", result.message);
      }
    } catch (error) {
      console.error("Error fetching asset details:", error);
      // Keep fields empty on error
    }
  };

  // Get asset type name by ID
  const getAssetTypeName = (typeId) => {
    const type = assetTypes.find((t) => t.ID === parseInt(typeId));
    return type ? type.ITEM : typeId;
  };

  return (
    <div className="container">
      <PageHeading
        title={"MASTER DATA - ASSETS"}
        right={
          <>
            <AddButton
              onClick={() => {
                setOpen(true);
                handleAddAsset();
              }}
            />
            <SaveButton
              title={loading ? "SAVING..." : "SAVE"}
              onClick={handleSaveAssets}
              disabled={loading}
            />
            <BackButton />
          </>
        }
      />
      {open && (
        <>
          <Table
            mt={30}
            headData={[
              "ASSET NAME",
              "DESCRIPTION",
              "TYPE",
              "PREVIOUS FY VALUE",
              "DEP %",
              "ACTIONS",
            ]}
            body={
              <>
                {addAssetData.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Input
                        placeholder={"Asset Name"}
                        value={item.asset_name}
                        onChange={(event) => {
                          setAddAssetData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              asset_name: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        placeholder={"Description"}
                        value={item.asset_description}
                        onChange={(event) => {
                          setAddAssetData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              asset_description: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <select
                        value={item.asset_type}
                        onChange={(event) => {
                          const assetTypeId = event.target.value;
                          setAddAssetData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              asset_type: assetTypeId,
                            };
                            return updatedData;
                          });
                          // Fetch asset details when asset type is selected
                          if (assetTypeId) {
                            fetchAssetDetails(assetTypeId, index);
                          }
                        }}
                        style={{
                          padding: "8px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          backgroundColor: "#fff",
                          color: "#333",
                          cursor: "pointer",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      >
                        <option value="">Select Type</option>
                        {assetTypes.map((type, idx) => (
                          <option key={idx} value={type.ID}>
                            {type.ITEM}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <Input
                        placeholder={"Value"}
                        type="number"
                        value={item.asset_value}
                        onChange={(event) => {
                          const value = event.target.value;
                          setAddAssetData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              asset_value: value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        placeholder={"Depreciation %"}
                        type="number"
                        step="0.01"
                        value={item.asset_dep_percent}
                        readOnly
                        style={{
                          backgroundColor: "#f5f5f5",
                          cursor: "not-allowed",
                        }}
                      />
                    </td>
                    <td>
                      <DeleteIcon onClick={() => handleAddDelete(item.id)} />
                    </td>
                  </tr>
                ))}
              </>
            }
          />
        </>
      )}
      <div style={{ height: "10px" }}></div>
      <div>
        <Table
          headData={[
            "ASSET NAME",
            "DESCRIPTION",
            "TYPE",
            "PREVIOUS FY VALUE",
            "DEP %",
            "CREATED DATE",
          ]}
          body={
            <>
              {assetListData && assetListData.length > 0 ? (
                assetListData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.ASSET_NAME}</td>
                    <td>{item.ASSET_DESCRIPTION}</td>
                    <td>{getAssetTypeName(item.ASSET_TYPE)}</td>
                    <td>
                      ₹
                      {parseFloat(item.ASSET_VALUE).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td>{parseFloat(item.ASSET_DEP_PERCENT).toFixed(2)}%</td>
                    <td>{moment(item.CREATED_DATE).format("DD/MM/YYYY")}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {listLoading ? "Loading..." : "No assets found"}
                  </td>
                </tr>
              )}
            </>
          }
        />
      </div>
      <Pagination2
        top={true}
        bottom={true}
        totalCount={totalCount}
        pageNo={pageNo}
        setPageNo={handlePageChange}
        entries={entries}
        setEntries={setEntries}
        SerchInput={searchValue}
        setSearchInput={handleSearchChange}
        search={true}
        count={5}
      />
      {alerts && (
        <SmallModal
          isOpen={alerts}
          setIsOpen={setAlerts}
          title={"alert"}
          mess={alertsMess}
          okbtn={setAlertsMess}
        />
      )}
    </div>
  );
};

export default MasterDataAssets;
